import React, { Children, cloneElement, Component, ContextType, ExoticComponent, isValidElement, ReactElement } from 'react';

import { CSSTransition } from 'react-transition-group';

import { Alignment, Placement } from '@philips-emr/tws-constants';
import { DocumentContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, element, expression, func, number, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { getBestPosition } from '@philips-emr/tws-utils';

import PopoverContent from './popover-content';

import './popover.scss';

export interface PopoverProps {
  alignment?: Alignment;
  children: ReactElement | ReactElement[] | ExoticComponent;
  className?: string;
  matchTargetWidth?: boolean;
  maxHeight?: number;
  onClose: (event: CloseEvent) => void;
  open: boolean;
  padding?: number;
  placement?: Placement;
  showArrow?: boolean;
  title?: string;
  zIndex?: number;
}

interface PopoverState {
  contentElement?: HTMLElement;
  targetElement?: HTMLElement;
}

class Popover extends Component<PopoverProps, PopoverState> {

  static Content = PopoverContent;
  static contextType = DocumentContext;

  static propTypes = {
    alignment: string,
    children: arrayOf(element).notEnumerable(),
    className: string,
    matchTargetWidth: bool,
    maxHeight: number,
    onClose: func,
    open: bool,
    padding: number,
    placement: string,
    showArrow: bool,
    title: expression,
    zIndex: number,
  };

  static defaultProps = {
    showArrow: true,
    matchTargetWidth: false,
    open: false,
  };

  context!: ContextType<typeof DocumentContext>;
  state: PopoverState = {};

  targetRef = (targetElement: HTMLElement): void => {
    this.setState({ targetElement });
  };

  contentRef = (contentElement: HTMLElement): void => {
    this.setState({ contentElement });
  };

  componentDidMount(): void {
    const { document } = this.context;

    document?.addEventListener('scroll', this.onScroll, true);
  }

  componentWillUnmount(): void {
    const { document } = this.context;

    document?.removeEventListener('scroll', this.onScroll, true);
  }

  onScroll = ({ target }: Event): void => {
    const { contentElement } = this.state;
    const { open, onClose } = this.props;

    if (!open || (target instanceof Node && contentElement?.contains(target))) {
      return;
    }

    onClose(new CloseEvent());
  };

  getContentWidth(): number | undefined {
    const { matchTargetWidth } = this.props;
    const { targetElement } = this.state;

    return matchTargetWidth && targetElement
      ? targetElement.offsetWidth
      : undefined;
  }

  renderTarget(target: ReactElement): ReactElement | null {
    if (!isValidElement(target)) {
      return null;
    }

    return cloneElement(target as ReactElement, {
      ref: this.targetRef,
    });
  }

  renderContent(content: ReactElement): ReactElement | null {
    if (!isValidElement(content)) {
      return null;
    }

    const { alignment, className, maxHeight, onClose, open, padding, placement, showArrow, title, zIndex } = this.props;
    const { targetElement, contentElement } = this.state;
    const { window, document } = this.context;

    const { placement: bestPlacement, ...position } = getBestPosition({
      alignment,
      placement,
      hostElement: targetElement,
      guestElement: contentElement,
      arrowSize: showArrow ? 12 : 0,
      window,
    });

    return cloneElement(content as ReactElement, {
      className,
      document,
      maxHeight,
      minWidth: this.getContentWidth(),
      onClose,
      onResize: this.onContentResize,
      open,
      padding,
      placement: bestPlacement,
      position: position,
      ref: this.contentRef,
      showArrow,
      title,
      zIndex,
    });
  }

  onContentResize = (): void => {
    this.forceUpdate();
  };

  render(): ReactElement {
    const { children, open } = this.props;

    const childrenArray = Children.toArray(children).flat();
    const content = childrenArray.find(child => child.type === Popover.Content);
    const target = childrenArray.find(child => child.type !== Popover.Content);

    return (
      <React.Fragment>
        {this.renderTarget(target)}

        <CSSTransition
          classNames="popover__fade"
          in={open}
          timeout={200}
          unmountOnExit
        >
          {this.renderContent(content)}
        </CSSTransition>
      </React.Fragment>
    );
  }
}

export default Popover;
